import type { ContributionEstimate } from './easy-ship';

export const ADVERTISING_SCENARIO_CLICKS = 100;
export const STANDARD_CONVERSION_RATES = [2, 5, 10, 15] as const;

export interface AdvertisingScenario {
  conversionRatePercentage: number;
  clicks: number;
  expectedOrders: number;
  advertisingSpend: number;
  advertisingCostPerOrder: number;
  grossRevenue: number;
  netRevenue: number;
  expectedProfit: number;
  expectedProfitMargin: number;
}

export function calculateAdvertisingScenario(
  costPerClick: number,
  conversionRatePercentage: number,
  sellingPrice: number,
  estimate: Pick<ContributionEstimate, 'expectedContribution' | 'netProductRevenue'>,
): AdvertisingScenario {
  if (!Number.isFinite(costPerClick) || costPerClick < 0) {
    throw new Error('Cost per click must be a non-negative finite number.');
  }
  if (
    !Number.isFinite(conversionRatePercentage) ||
    conversionRatePercentage <= 0 ||
    conversionRatePercentage > 100
  ) {
    throw new Error('Conversion rate must be greater than 0% and no more than 100%.');
  }
  if (!Number.isFinite(sellingPrice) || sellingPrice <= 0) {
    throw new Error('Selling price must be a positive finite number.');
  }

  const clicks = ADVERTISING_SCENARIO_CLICKS;
  const expectedOrders = clicks * (conversionRatePercentage / 100);
  const advertisingSpend = clicks * costPerClick;
  const grossRevenue = expectedOrders * sellingPrice;
  const netRevenue = expectedOrders * estimate.netProductRevenue;
  const expectedProfit = expectedOrders * estimate.expectedContribution - advertisingSpend;

  return {
    conversionRatePercentage,
    clicks,
    expectedOrders,
    advertisingSpend,
    advertisingCostPerOrder: advertisingSpend / expectedOrders,
    grossRevenue,
    netRevenue,
    expectedProfit,
    expectedProfitMargin: (expectedProfit / netRevenue) * 100,
  };
}
